import { CircularProgress } from "@mui/material";
import axios from "axios";
import React, {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import {
    getAccessibleCompanyByName,
    getAccessiblePosProfiles,
    isRoleAdmin,
} from "./frappeConfig";
import CompanyProfileSelection from "./pages/CompanyProfileSelection/CompanyProfileSelection";
import { useContacts, useCustomers } from "./shared/hooks/useCustomer";
import {
    useGetDocTypeList,
    useGetDocTypeListWithChildren,
} from "./shared/hooks/useDocTypesData";
import {
    useFrappeToken,
    useGetUserSettings,
    useSaveUserSettings,
    useLoadDoctype,
    useSiteName,
} from "./shared/hooks/useFrappeDoctype";
import { useAddons, useItems } from "./shared/hooks/useItemsData";
import { useListDocTypes } from "./shared/hooks/useListDocTypesData";
import { useGetLoggedUser } from "./shared/hooks/useUser";

// Create the context
export const AppContext = createContext();

// Create a provider component
export const AppProvider = ({ children }) => {
    const [globalState, setGlobalState] = useState({
        defaultCurrency: null,
        permissions: null,
        branchName: null,
        location: null,
        loggedInUser: null,
        paymentAccounts: null,
    });

    const { data: frappeToken } = useFrappeToken(true);

    useEffect(() => {
        if (frappeToken) {
            axios.defaults.headers.common[
                "x-frappe-csrf-token"
            ] = `${frappeToken}`;
        }
    }, [frappeToken]);

    const [tablesMoving, setTablesMoving] = useState(false);

    const [multipleCompanies, setMultipleCompanies] = useState(false);

    const [showClaimTypes, setShowClaimTypes] = useState(false);
    const [isTabsPos, setIsTabsPos] = useState(false);

    const [isAdmin, setAdmin] = useState(false);

    const [userInfo, setUserInfo] = useState({});

    const { data: userInfoData } = useGetLoggedUser(true);

    const isKitchenUser = useMemo(() => {
        const role =
            userInfoData?.role_profile_name || userInfo?.role_profile_name;
        return role === "Kitchen";
    }, [userInfoData, userInfo]);

    const isCallCenterUser = useMemo(() => {
        const role =
            userInfoData?.role_profile_name || userInfo?.role_profile_name;
        return role === "Call Center";
    }, [userInfoData, userInfo]);

    const workMode = useMemo(() => {
        return isCallCenterUser ? "call_center" : "cash";
    }, [isCallCenterUser]);

    const { data: menuAddons } = useAddons();

    const [company, setCompany] = useState({});

    const { data: profileContext } = useLoadDoctype(
        "Profile Context",
        "Profile Context"
    );
    const { data: sitename } = useSiteName();

    const [socket, setSocket] = useState(null);

    useEffect(() => {
        if (sitename) {
            const supportsWebSocket =
                typeof window !== "undefined" && "WebSocket" in window;
            if (!supportsWebSocket) {
                setSocket(null);
                return;
            }

            const socketClient = io(
                window.location.origin.replace("https://", "wss://") +
                    `/${sitename}`,
                {
                    transports: ["websocket"],
                    reconnection: true,
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 10000,
                    timeout: 20000,
                }
            );
            setSocket(socketClient);

            socketClient.on("connect", () => {
                console.log("connected to socket");
            });

            const reconnectSocket = () => {
                if (!socketClient.connected) {
                    socketClient.connect();
                }
            };

            const reconnectWhenVisible = () => {
                if (document.visibilityState === "visible") {
                    reconnectSocket();
                }
            };

            window.addEventListener("online", reconnectSocket);
            document.addEventListener("visibilitychange", reconnectWhenVisible);

            return () => {
                window.removeEventListener("online", reconnectSocket);
                document.removeEventListener(
                    "visibilitychange",
                    reconnectWhenVisible
                );
                socketClient.disconnect();
                setSocket(null);
            };
        }
    }, [sitename]);

    useEffect(() => {
        if (profileContext) {
            const _multipleCompanies =
                profileContext.docs?.[0]?.profile_doctype == "Company";
            setMultipleCompanies(_multipleCompanies);
        }
    }, [profileContext]);

    const { data: getAddresses } = useGetDocTypeList({
        doctype: "Address",
    });

    const { data: getPosProfileData, isLoading: isLoadingPosProfile } =
        useGetDocTypeListWithChildren({
            doctype:
                company && !isKitchenUser && userInfo.name
                    ? "POS Profile"
                    : null,
            filters: company ? [["company", "=", company.name]] : null,
        });
    const { data: posProfileUserSettings } = useGetUserSettings({
        doctype: "POS Profile",
    });

    // frappe.client.get_list applies the logged-in user's Company permissions.
    const { data: companies } = useListDocTypes(
        "getCompanies",
        isKitchenUser ? null : "Company",
        "",
        {},
        ["*"]
    );

    const { data: contacts } = useContacts();
    const { data: customers } = useCustomers();
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    const [posProfiles, setPosProfiles] = useState([]);
    const [posProfile, setPosProfileState] = useState(null);
    const [discountAmountAllowed, setDiscountAmountAllowed] = useState(false);

    useEffect(() => {
        if (posProfile) {
            setDiscountAmountAllowed(posProfile.allow_discount_change == 1);
        }
    }, [posProfile]);

    const [userMenuSettings, setUserMenuSettings] = useState(null);
    const userMenuSettingsRef = useRef(null);
    const saveMenuSettings = useSaveUserSettings();
    const menuSettingsQueryClient = useQueryClient();

    useEffect(() => {
        userMenuSettingsRef.current = userMenuSettings;
    }, [userMenuSettings]);

    const { data: userSettings } = useGetUserSettings({
        doctype: "menu_settings",
    });

    /**
     * Centralized, race-safe updater for `menu_settings` user-settings.
     *
     * Why this exists:
     *   The Frappe `user_settings.save` endpoint replaces the entire JSON blob
     *   for the (user, doctype) pair. Callers used to build the payload from
     *   their own closure of `userMenuSettings`, so two rapid saves (e.g.
     *   reorder group then reorder item) would each capture the same stale
     *   snapshot and the second one would overwrite the first one's field on
     *   the server. By funnelling every change through a ref-backed merge we
     *   guarantee each save ships the latest known state.
     *
     * Flow:
     *   1. Merge `patch` into the latest known settings (ref, not closure).
     *   2. Update local context state and the react-query cache so any
     *      consumer / refetch sees the new value immediately.
     *   3. POST the full merged blob; rollback ref + cache on error.
     */
    const updateUserMenuSettings = useCallback(
        (patch) => {
            if (!patch || typeof patch !== "object") return;

            const previous = userMenuSettingsRef.current || {};
            const next = { ...previous, ...patch };

            userMenuSettingsRef.current = next;
            setUserMenuSettings(next);
            menuSettingsQueryClient.setQueryData(
                ["GetUserSettings", "menu_settings"],
                next
            );

            saveMenuSettings.mutate(
                {
                    doctype: "menu_settings",
                    user_settings: next,
                },
                {
                    onError: (err) => {
                        userMenuSettingsRef.current = previous;
                        setUserMenuSettings(previous);
                        menuSettingsQueryClient.setQueryData(
                            ["GetUserSettings", "menu_settings"],
                            previous
                        );
                        console.error(
                            "Failed to save menu_settings, rolled back",
                            err
                        );
                    },
                }
            );
        },
        [menuSettingsQueryClient, saveMenuSettings]
    );

    const {
        data: companyUserSettings,
        isLoading: isCompanyUserSettingsLoading,
        isFetched: isCompanyUserSettingsFetched,
    } = useGetUserSettings({
        doctype: "Company",
    });

    const saveCompanyUserSettings = useSaveUserSettings();
    const savePosProfileUserSettings = useSaveUserSettings();

    const switchCompany = useCallback(
        async (companyName) => {
            const selectedCompany = getAccessibleCompanyByName(
                companies,
                companyName
            );

            if (!selectedCompany) {
                throw new Error("You do not have access to this company");
            }

            if (selectedCompany.name === company?.name) {
                return selectedCompany;
            }

            await saveCompanyUserSettings.mutateAsync({
                doctype: "Company",
                user_settings: {
                    ...(companyUserSettings || {}),
                    selectedProfile: selectedCompany.name,
                },
            });

            // Prevent the previous company's POS Profile from being used while
            // the application reloads the newly selected company context.
            setPosProfileState(null);
            setCompany(selectedCompany);
            return selectedCompany;
        },
        [
            companies,
            company?.name,
            companyUserSettings,
            saveCompanyUserSettings,
        ]
    );

    useEffect(() => {
        if (!companies || companies.length === 0) return;
        // Prevent race: don't choose/persist fallback before user settings are fetched.
        if (!isCompanyUserSettingsFetched || isCompanyUserSettingsLoading) return;

        const fallbackCompany = companies[0];
        const preferredCompanyName = companyUserSettings?.selectedProfile;
        const resolvedCompany =
            companies.find((entry) => entry.name === preferredCompanyName) ||
            fallbackCompany;

        setCompany((prev) =>
            prev?.name === resolvedCompany?.name ? prev : resolvedCompany
        );

        // Persist only when missing/invalid to avoid rewriting existing valid user choice.
        if (!preferredCompanyName || preferredCompanyName !== resolvedCompany.name) {
            saveCompanyUserSettings.mutate({
                doctype: "Company",
                user_settings: {
                    ...(companyUserSettings || {}),
                    selectedProfile: resolvedCompany.name,
                },
            });
        }
    }, [
        companies,
        companyUserSettings,
        isCompanyUserSettingsFetched,
        isCompanyUserSettingsLoading,
    ]);

    const { data: settingsDetails, status: restaurantSettingsStatus } =
        useLoadDoctype(
            "Restaurant Settings",
            "Restaurant Settings"
        );

    const foodLateTimeEnabled = useMemo(() => {
        if (settingsDetails) {
            return settingsDetails?.docs[0]?.order_late_time > 0;
        }
        return false;
    }, [settingsDetails]);

    const foodLateTime = useMemo(() => {
        if (foodLateTimeEnabled) {
            return settingsDetails?.docs[0]?.order_late_time;
        }
        return 0;
    }, [foodLateTimeEnabled, settingsDetails]);

    const elevatedPrivileges =
        settingsDetails?.docs[0]?.allow_elevated_privileges == 1;
    const isRetailPos = Number(settingsDetails?.docs[0]?.is_retail_pos) === 1;

    const [serviceFee, setServiceFee] = useState(0);

    // useEffect(() => {
    //     if (getCompanyData) {
    //         setCompany(getCompanyData[0]);
    //     }
    // }, [getCompanyData]);

    useEffect(() => {
        if (userSettings) {
            setUserMenuSettings(userSettings);
        }
    }, [userSettings]);

    useEffect(() => {
        if (settingsDetails) {
            const settings = settingsDetails.docs[0];
            const { detailed_daily_expenses, is_tabs_pos, service_charge } =
                settings;

            if (service_charge && service_charge > 0) {
                setServiceFee(service_charge);
            }

            if (detailed_daily_expenses) {
                setShowClaimTypes(true);
            } else {
                setShowClaimTypes(false);
            }
            if (is_tabs_pos == 1) {
                setIsTabsPos(true);
            } else {
                setIsTabsPos(false);
            }
        }
    }, [settingsDetails]);

    const setPosProfile = useCallback(
        (profileOrName, persist = true) => {
            if (!profileOrName) return;

            const profileName =
                typeof profileOrName === "string"
                    ? profileOrName
                    : profileOrName.name;
            const selected =
                posProfiles.find((p) => p.name === profileName) ||
                (typeof profileOrName === "object" ? profileOrName : null);

            if (!selected) return;

            setPosProfileState(selected);
            if (!persist) return;

            savePosProfileUserSettings.mutate({
                doctype: "POS Profile",
                user_settings: {
                    ...(posProfileUserSettings || {}),
                    selectedProfile: selected.name,
                },
            });
        },
        [posProfiles, posProfileUserSettings, savePosProfileUserSettings]
    );

    useEffect(() => {
        const profiles = getAccessiblePosProfiles(
            getPosProfileData,
            userInfo?.name,
            userInfo?.role_profile_name
        );
        setPosProfiles(profiles);

        if (!profiles.length) {
            setPosProfileState(null);
            return;
        }

        const preferredProfileName = posProfileUserSettings?.selectedProfile;
        const currentProfileName = posProfile?.name;
        const selectedProfile =
            // Keep immediate local selection stable; don't let stale user settings override it.
            profiles.find((p) => p.name === currentProfileName) ||
            profiles.find((p) => p.name === preferredProfileName) ||
            profiles[0];

        setPosProfileState(selectedProfile);
    }, [
        getPosProfileData,
        posProfileUserSettings?.selectedProfile,
        userInfo?.name,
        userInfo?.role_profile_name,
    ]);

    useEffect(() => {
        if (userInfoData) {
            setUserInfo(userInfoData);
        }
    }, [userInfoData]);

    useEffect(() => {
        if (
            userInfo.role_profile_name &&
            isRoleAdmin(userInfo.role_profile_name || userInfo.full_name)
        ) {
            setAdmin(true);
        }
    }, [userInfo]);

    const [loadingTable, setLoadingTable] = useState("");

    const [selectedTableName, setSelectedTableName] = useState();
    const [selectedTable, setSelectedTable] = useState({});

    const setTable = (tableName) => {
        setSelectedTableName(tableName);
    };

    const posProfileItemGroups = useMemo(() => {
        return (
            posProfile?.item_groups
                ?.map((item) => item.item_group)
                ?.filter(Boolean) || []
        );
    }, [posProfile]);

    // Scenarios:
    // 1) multipleCompanies=false => treat item_groups as whitelist, but root stays "All Item Groups"
    // 2) multipleCompanies=true + no item_groups => no filtering, root is "All Item Groups"
    // 3) multipleCompanies=true + many item_groups => whitelist filtering, root is "All Item Groups"
    // 4) multipleCompanies=true + exactly one item_group => that group becomes the root for this company's menu
    const menuAllowedGroups = useMemo(() => {
        return posProfileItemGroups.length > 0 ? posProfileItemGroups : null;
    }, [posProfileItemGroups]);

    const menuRootGroupId = useMemo(() => {
        return multipleCompanies && posProfileItemGroups.length === 1
            ? posProfileItemGroups[0]
            : "All Item Groups";
    }, [multipleCompanies, posProfileItemGroups]);

    const { data: menuItems, refetch: refetchMenuItems } = useItems({
        allowedGroups: menuAllowedGroups,
        isRetailPos,
        posProfile,
        enabled: restaurantSettingsStatus === "success",
    });

    const getItem = useCallback(
        (item) => {
            if (menuAddons && menuItems) {
                return [...menuItems, menuAddons].find(
                    (i) => i.item_code === item
                );
            }
            return null;
        },
        [menuItems, menuAddons]
    );

    const getMenuItem = (item) => {
        if (menuItems) {
            return menuItems.find((i) => i.item_code === item);
        } else {
            return null;
        }
    };

    // Determine what to render based on company state
    const renderContent = () => {
        // Kitchen users don't require company context to use KDS screens
        if (isKitchenUser) {
            return children;
        }

        // If we're still loading companies or company user settings, show loading
        if (
            !companies ||
            !userInfo ||
            isLoadingPosProfile ||
            isCompanyUserSettingsLoading
        ) {
            return <CircularProgress />;
        }

        // If there's only one company, auto-select it
        if (companies.length === 1 && !company?.name) {
            return <CircularProgress />;
        }

        // If there are multiple companies and no company is selected (or the selection is invalid), show company selection
        if (companies.length > 1 && !company.name) {
            return <CompanyProfileSelection />;
        }

        // If we have a company selected, render the children
        if (company) {
            return children;
        }

        // Default loading state
        return <CircularProgress />;
    };

    return (
        <AppContext.Provider
            value={{
                globalState,
                setGlobalState,
                tableContext: [selectedTable, setSelectedTable],
                customerContext: [selectedCustomer, setSelectedCustomer],
                setTable,
                foodLateTime,
                foodLateTimeEnabled,
                tablesMovingContext: [tablesMoving, setTablesMoving],
                menuItems,
                getMenuItem,
                getItem,
                contacts: contacts,
                customers,
                loadingTable,
                setLoadingTable,
                isKitchenUser,
                isCallCenterUser,
                workMode,
                menuAddons,
                userInfo,
                company,
                switchCompany,
                posProfile,
                posProfiles,
                setPosProfile,
                menuAllowedGroups,
                menuRootGroupId,
                userMenuSettings,
                setUserMenuSettings,
                updateUserMenuSettings,
                showClaimTypes,
                isTabsPos,
                isRetailPos,
                addresses: getAddresses || [],
                companies, // Add companies to context for CompanyProfileSelection
                multipleCompanies,
                settingsDetails,
                restaurantSettings: settingsDetails,
                restaurantSettingsStatus,
                serviceFee,
                refetchMenuItems,
                socket,
                elevatedPrivileges,
                discountAmountAllowed,
            }}
        >
            {renderContent()}
        </AppContext.Provider>
    );
};
